import { useQuery } from '@tanstack/react-query'
import { personaWorkshopApi } from '@/lib/api/client'

export interface PersonaLookupEntry {
  name: string
  role: string
  photo?: string
  sessionId: string
  primaryNeed?: string
  keyPain?: string
}

/**
 * Returns a Map keyed by lowercase persona name → persona metadata from workshop sessions.
 * Used to enrich requirement rows at render time so real persona photos show up
 * even for requirements generated before the photo was persisted into metadata.
 */
export function usePersonaLookup(accountId: string, projectId: string) {
  return useQuery({
    queryKey: ['personaLookup', accountId, projectId],
    queryFn: async (): Promise<Map<string, PersonaLookupEntry>> => {
      const sessions = await personaWorkshopApi.listSessions(accountId, projectId) as Array<{
        id: string
        personaData: {
          name?: string
          role?: string
          photo?: string
          jtbds?: Array<{
            functional?: string
            emotional?: string
            pains?: Array<{ description?: string }>
          }>
          goals?: string[]
        }
      }>

      const map = new Map<string, PersonaLookupEntry>()
      if (!Array.isArray(sessions)) return map

      for (const s of sessions) {
        const d = s.personaData
        if (!d?.name?.trim()) continue
        const firstJtbd = d.jtbds?.[0]
        const primaryNeed = firstJtbd?.functional || firstJtbd?.emotional || d.goals?.[0]
        const keyPain = firstJtbd?.pains?.[0]?.description
        const entry: PersonaLookupEntry = {
          name: d.name.trim(),
          role: d.role || 'Product user',
          photo: d.photo || undefined,
          sessionId: s.id,
          primaryNeed,
          keyPain,
        }
        map.set(entry.name.toLowerCase(), entry)
      }
      return map
    },
    enabled: !!accountId && !!projectId,
    staleTime: 5 * 60 * 1000,
  })
}

/**
 * Index persona workshop sessions by their sessionId (same as requirement_persona_links.persona_session_id).
 * Used by the List + Notes views to hydrate persona groups with real names, roles, and photos.
 */
export function buildPersonaIndexBySessionId(
  lookup: Map<string, PersonaLookupEntry> | undefined,
): Map<string, { name: string; role?: string; color?: string; initials?: string; photo?: string }> {
  const out = new Map<string, { name: string; role?: string; color?: string; initials?: string; photo?: string }>()
  if (!lookup) return out
  for (const p of lookup.values()) {
    const initials = p.name
      .split(/\s+/).filter(Boolean)
      .map(word => word[0]?.toUpperCase() ?? '')
      .slice(0, 2).join('') || '??'
    out.set(p.sessionId, {
      name: p.name,
      role: p.role,
      initials,
      photo: p.photo,
      color: 'var(--color-market)',
    })
  }
  return out
}

/** Fuzzy-match a persona name (case-insensitive, first-word + substring fallback). */
export function matchPersonaLookup(
  rawName: string | undefined,
  lookup: Map<string, PersonaLookupEntry> | undefined,
): PersonaLookupEntry | null {
  if (!rawName || !lookup) return null
  const name = rawName.trim().toLowerCase()
  if (!name) return null
  if (lookup.has(name)) return lookup.get(name)!
  const firstWord = name.split(/[\s·,]/)[0]
  if (firstWord && lookup.has(firstWord)) return lookup.get(firstWord)!
  for (const [key, persona] of lookup) {
    if (name.includes(key) || key.includes(name)) return persona
  }
  return null
}
